import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTenantSubdomain, updateCurrentUserProfile } from '../services/api';
import i18n, { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, normalizeLanguage } from '../i18n';

const LANGUAGE_STORAGE_PREFIX = 'followus_language';
const LANGUAGE_PREFERENCE_LS_KEY = (tenant) =>
  tenant ? `${LANGUAGE_STORAGE_PREFIX}_tenant_${tenant}` : LANGUAGE_STORAGE_PREFIX;

function getBrowserLanguage() {
  try {
    if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
    const candidates = [
      ...(navigator.languages || []),
      navigator.language,
    ].filter(Boolean);
    for (const candidate of candidates) {
      const code = normalizeLanguage(candidate);
      if (SUPPORTED_LANGUAGES.includes(code) && candidate.toLowerCase().startsWith(code)) {
        return code;
      }
    }
  } catch (_) {}
  return DEFAULT_LANGUAGE;
}

function getStoredLanguage() {
  try {
    if (typeof window === 'undefined') return null;
    const tenant = getTenantSubdomain();
    const key = LANGUAGE_PREFERENCE_LS_KEY(tenant);
    const stored = localStorage.getItem(key);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch (_) {}
  return null;
}

function resolveInitialLanguage() {
  return getStoredLanguage() || getBrowserLanguage() || DEFAULT_LANGUAGE;
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { i18n: i18nInstance } = useTranslation();
  const [language, setLanguageState] = useState(() => {
    const initial = resolveInitialLanguage();
    if (i18n.language !== initial) {
      i18n.changeLanguage(initial);
    }
    return initial;
  });

  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setLanguageState(normalizeLanguage(lng));
    };
    i18nInstance.on('languageChanged', handleLanguageChanged);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, [i18nInstance]);

  const setLanguage = (value) => {
    const next = normalizeLanguage(value);
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    setLanguageState(next);
    try {
      const tenant = getTenantSubdomain();
      const key = LANGUAGE_PREFERENCE_LS_KEY(tenant);
      localStorage.setItem(key, next);
    } catch (_) {}
    i18n.changeLanguage(next);
    try {
      updateCurrentUserProfile({ language_preference: next }).catch(() => {});
    } catch (_) {}
  };

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES,
    }),
    [language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}

export { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, LANGUAGE_PREFERENCE_LS_KEY };
